import React from 'react'
import { css } from '@emotion/core'
import Select, { components, ValueType, OptionsType, GroupedOptionsType } from 'react-select'
import AsyncSelect from 'react-select/async'
import AsyncCreatableSelect from 'react-select/async-creatable'
import { useTheme } from 'emotion-theming'
import SVGIcon from 'components/common/atoms/SVGIcon'
import ChevronSVG from 'svgs/chevron.svg'
import CheckedSVG from 'svgs/checked.svg'
import Badge from 'components/common/atoms/Badge'

export interface Option {
  label?: string
  value?: string
  withBadge?: boolean
  isDisabled?: boolean
}

interface SelectProps {
  field?: {}
  touched?: boolean
  error?: boolean
  name?: string
  label?: string
  isDisabled?: boolean
  defaultOptions?: boolean | OptionsType<Option>
  value?: Option | null
  placeholder?: string
  options?: OptionsType<Option> | GroupedOptionsType<Option>
  badgeLabel?: string
  isClearable?: boolean
  type?: 'async' | 'asyncCreatable' | 'default'
  customNoOptionsMessage?: string
  customNotFoundMessage?: string
  loadOptions?: (inputValue: string) => void | Promise<any>
  onInputChange?: () => void
  onChange?: (value: ValueType<Option, boolean>) => void
  onMenuOpen?: () => void
  onBlur?: () => void
  onFocus?: () => void
  menuIsOpen?: boolean
  isSearchable?: boolean
}

const SelectStyle = (theme: ThemeType) => css`
  .select-input {
    &.has-no-value {
      .select__control {
        background-color: ${theme.gray.gray10};
        color: ${theme.gray.gray40};
        border-color: ${theme.gray.gray20};
      }
    }
    &.is-error {
      .select__control {
        border: solid 1px ${theme.error.red40};
        background-color: ${theme.error.red10};
        &:hover {
          border-color: ${theme.error.red40} !important;
        }
        &--is-focused {
          box-shadow: 0 0 0 0.5px ${theme.gray.gray50};
        }
      }
    }
    .select__control {
      transition: border-radius 0.3s ease;
      border-color: ${theme.gray.gray30};
      height: 48px;
      &--is-disabled {
        background-color: ${theme.gray.gray20};
        .select__indicator {
          svg,
          svg path {
            fill: ${theme.gray.gray30};
          }
        }
      }
      &--is-focused {
        border-color: ${theme.gray.gray40};
        box-shadow: 0 1px 6px 0 rgba(0, 0, 0, 0.3);
      }
      &:hover {
        border-color: ${theme.gray.gray40};
      }
      &--menu-is-open {
        box-shadow: 0 1px 6px 0 rgba(0, 0, 0, 0.3);
        border-bottom-left-radius: 0px;
        border-bottom-right-radius: 0px;
        .select__indicator {
          transform: rotate(180deg);
        }
      }
    }
    .select__placeholder,
    .select__single-value,
    .select__input {
      font-size: 14px;
      margin: 0;
      color: ${theme.gray.gray60};
    }
    .select__indicator {
      transition: transform 0.3s;
      padding: 16px;
      svg {
        width: 16px;
        height: 16px;
        fill: ${theme.gray.gray50};
      }
    }
    .select__option {
      padding: 0;
      margin: 0;
      width: 100%;
      .icon-wrapper {
        justify-self: flex-end;
      }
      .select__option-item {
        height: 40px;
        margin: 0;
        padding: 8px 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        span {
          font-size: 14px;
          flex: 1;
        }
        .badge {
          margin-left: 4px;
        }
      }
      &:hover,
      &--is-focused {
        background-color: transparent;
        .select__option-item {
          background-color: ${theme.gray.gray20};
        }
      }
      &--is-selected {
        background-color: transparent;
        color: ${theme.gray.gray60};
      }
      &--is-disabled {
        background-color: transparent;
        pointer-events: none;
        .select__option-item {
          span {
            color: ${theme.gray.gray30};
          }
          &.with-badge {
            justify-content: stretch;
            span {
              flex: 1;
            }
          }
        }
      }
    }
    .select__value-container {
      height: 28px;
      padding: 0 0 0 16px;
    }
    .select__placeholder {
      color: ${theme.gray.gray40};
    }
    .select__menu {
      margin-top: 0;
      border-top-left-radius: 0px;
      border-top-right-radius: 0px;
      border: solid 1px ${theme.gray.gray40};
      border-top: none;
      box-shadow: 0 1px 6px 0 rgba(0, 0, 0, 0.3);
      animation: fade--in 0.3s ease-in-out;
      z-index: 5;
    }
    @keyframes fade--in {
      0% {
        opacity: 0;
        transform: translateY(-8px);
      }

      100% {
        opacity: 1;
        transform: translateY(0);
      }
    }
    .select__menu-notice--no-options {
      padding: 16px;
      color: ${theme.gray.gray40};
      .select__option {
        font-size: 14px;
        line-height: 21px;
        color: ${theme.gray.gray40};

        p {
          margin-top: 4px;
          font-size: 12px;
          line-height: 18px;
          color: ${theme.gray.gray40};
          font-weight: normal;
        }
        @media screen and (min-width: 768px) {
          font-size: 16px;
          line-height: 24px;
          p {
            font-size: 14px;
            line-height: 21px;
          }
        }
      }
    }
    .select__menu-list {
      padding: 0;
      &:hover::-webkit-scrollbar-thumb,
      &::-webkit-scrollbar-thumb:hover {
        border-radius: 12px;
        box-shadow: inset 0 0 12px 12px #d3dce6;
        border: solid 4px transparent;
      }
      &::-webkit-scrollbar {
        background-color: transparent;
        width: 12px;
      }
      > .is-clearable:first-of-type span {
        color: ${theme.gray.gray50};
      }
    }
    .select__input input {
      color: ${theme.gray.gray60} !important;
      position: absolute;
      top: 3px;
      left: 16px;
    }
  }
`

const DropdownIndicator = (props: any) => (
  <components.DropdownIndicator {...props}>
    <ChevronSVG />
  </components.DropdownIndicator>
)

const OptionComponent = (props: any) => {
  const theme: ThemeType = useTheme()
  return (
    <components.Option className={`${props.selectProps.isClearable ? 'is-clearable' : ''}`} {...props}>
      <div className={`select__option ${props.data.isDisabled ? 'select__option--is-disabled' : ''}`}>
        <div className={`select__option-item ${props.data.withBadge ? 'with-badge' : ''} `}>
          <span>{props.label}</span>
          {props.isSelected && (
            <SVGIcon name="checked" size="xs" color={theme.campus.aqua30}>
              <CheckedSVG />
            </SVGIcon>
          )}
          {props.data.withBadge && (
            <Badge
              background={theme.neutral.white}
              borderColor={theme.gray.gray30}
              text={props.selectProps.badgeLabel || ''}
              color={theme.gray.gray60}
            />
          )}
        </div>
      </div>
    </components.Option>
  )
}

const Menu = (props: any) => {
  const { selectProps, options } = props
  if (selectProps.AsyncSelect && options.length === 0) {
    const defaultNoOptionsMsg = `Masukkan ${selectProps.label} untuk melakukan pencarian`
    return (
      <components.Menu className="select__menu-notice--no-options" {...props}>
        <div className="select__option">
          {selectProps.inputValue
            ? selectProps.defaultNotFoundMsg
            : selectProps.customNoOptionsMessage || defaultNoOptionsMsg}
          {selectProps.inputValue && selectProps.customNotFoundMessage && <p>{selectProps.customNotFoundMessage}</p>}
        </div>
      </components.Menu>
    )
  }
  return <components.Menu {...props}>{props.children}</components.Menu>
}

const SelectComponent = {
  IndicatorSeparator: () => null,
  LoadingIndicator: () => null,
  ClearIndicator: () => null,
  Menu,
  DropdownIndicator,
  Option: OptionComponent,
}

const SelectInput = (props: SelectProps) => {
  const theme: ThemeType = useTheme()
  const {
    type = 'default',
    defaultOptions,
    options,
    error,
    touched,
    isDisabled,
    onChange,
    placeholder,
    customNoOptionsMessage = '',
    customNotFoundMessage = '',
    loadOptions = () => undefined,
    value,
    onBlur,
    isClearable,
    menuIsOpen,
    isSearchable,
    onFocus,
    ...otherProps
  } = props

  const placeholderOption = {
    label: placeholder || 'Pilih...',
    value: '',
    isDisabled: false,
    withBadge: false,
  }
  const placeholderOptions = [placeholderOption, ...(Array.isArray(options) ? options : [options])]
  const customOptions = isClearable ? placeholderOptions : options
  const defaultNotFoundMsg = 'Kata kunci tidak ditemukan'
  const noOptionsMessage = () => customNoOptionsMessage || 'Pilihan tidak ditemukan'
  const loadingMessage = () => 'Mencari...'
  const hasValue = Boolean(value)
  const classNames = `select-input${error && touched ? ' is-error' : ''} ${!hasValue ? ' has-no-value' : ''}`

  if (type === 'async') {
    return (
      <span css={SelectStyle(theme)}>
        <AsyncSelect
          cacheOptions
          loadOptions={loadOptions}
          options={customOptions}
          onChange={onChange}
          defaultOptions={defaultOptions}
          isDisabled={isDisabled}
          placeholder={placeholder || 'Pilih'}
          value={value}
          onBlur={onBlur}
          className={classNames}
          classNamePrefix="select"
          components={SelectComponent}
          loadingMessage={loadingMessage}
          defaultNotFoundMsg={defaultNotFoundMsg || ''}
          customNoOptionsMessage={customNoOptionsMessage || ''}
          customNotFoundMessage={customNotFoundMessage || ''}
          isClearable={isClearable}
          AsyncSelect
          isSearchable
          {...otherProps}
        />
      </span>
    )
  } else if (type === 'asyncCreatable') {
    return (
      <span css={SelectStyle(theme)}>
        <AsyncCreatableSelect
          cacheOptions
          loadOptions={loadOptions}
          options={customOptions}
          onChange={onChange}
          onBlur={onBlur}
          defaultOptions={defaultOptions}
          isDisabled={isDisabled}
          placeholder={placeholder || 'Pilih'}
          value={value}
          className={classNames}
          classNamePrefix="select"
          components={SelectComponent}
          loadingMessage={loadingMessage}
          customNoOptionsMessage={customNoOptionsMessage || ''}
          isClearable={isClearable}
          formatCreateLabel={(userInput) => `Tambah ${otherProps.label} "${userInput}"`}
          AsyncSelect
          isSearchable
          {...otherProps}
        />
      </span>
    )
  }
  return (
    <span css={SelectStyle(theme)}>
      <Select
        defaultOptions={defaultOptions}
        options={customOptions}
        onChange={onChange}
        onBlur={onBlur}
        isDisabled={isDisabled}
        placeholder={placeholder || 'Pilih'}
        value={value}
        className={classNames}
        classNamePrefix="select"
        components={SelectComponent}
        noOptionsMessage={noOptionsMessage}
        isClearable={isClearable}
        menuIsOpen={menuIsOpen}
        isSearchable={isSearchable}
        onFocus={onFocus}
        {...otherProps}
      />
    </span>
  )
}

export default SelectInput
